/*
    🔥 APP HEADER UTILITIES
        Registro central de utilidades disponibles en el header.
        La visibilidad final se obtiene desde APP_LAYOUT_STRUCTURE.utilities, cuyos
        interruptores provienen de variables de entorno.

    ----------------------------------------------------------------------------
    🔥 REGISTRY
        Cada id registrado conoce únicamente cómo renderizar su utilidad.
        Activar o desactivar una herramienta no requiere modificar este archivo:
        se controla mediante APP_CONFIG_GLOBAL.UTILITIES.*.

    ----------------------------------------------------------------------------
    🔥 UTILIDADES CON PANEL
        language, theme, notifications y profile utilizan componentes completos.

    ----------------------------------------------------------------------------
    🔥 UTILIDADES DE NAVEGACIÓN
        carts, favs, messages, email y WhatsApp utilizan UtilityLink y toman su
        destino desde APP_LAYOUT_STRUCTURE.utilityLinks.

    ----------------------------------------------------------------------------
    🔥 ACCESOS DE AUTENTICACIÓN
        auth_login representa el acceso normal de la aplicación.
        auth_portal representa un acceso independiente al portal de clientes.
        Ambos se muestran únicamente sin sesión y poseen flags globales separados.
*/

import { FaComments, FaEnvelope, FaHeart, FaShoppingCart, FaWhatsapp } from 'react-icons/fa';
import { Profile } from './Plugins/_Profile';
import { Language } from './Plugins/_Language';
import { ThemeApp } from './Plugins/_ThemeApp';
import { AuthAccess } from './Plugins/_AuthAccess';
import { Notifications } from './Plugins/_Notifications';
import { UtilityLink } from './UtilityDropdown';
import { HeaderUtilitiesProvider } from './_Hooks/useHeaderUtilities';
import { APP_LAYOUT_STRUCTURE } from '../../AppLayoutStructure';
import { useLanguage } from '../../../../../_CORE/Contexts/Translate/useLanguageContext';

const LINKS = APP_LAYOUT_STRUCTURE.utilityLinks ?? {};

const countOf = (data, key) => Number(data?.[key]?.count ?? data?.[key] ?? 0) || 0;

const REGISTRY = {
    carts: (props) => <UtilityLink to={LINKS.carts} icon={<FaShoppingCart size={18} />} label="Carrito" badge={countOf(props.data, 'carts')} />,
    favs: (props) => <UtilityLink to={LINKS.favs} icon={<FaHeart size={18} />} label="Favoritos" badge={countOf(props.data, 'favs')} />,
    messages: (props) => <UtilityLink to={LINKS.messages} icon={<FaComments size={18} />} label="Conversaciones" badge={countOf(props.data, 'messages')} />,
    messages_email: (props) => <UtilityLink to={LINKS.messages_email} icon={<FaEnvelope size={18} />} label="Email" badge={countOf(props.data, 'messages_email')} />,
    messages_whatsapp: (props) => <UtilityLink to={LINKS.messages_whatsapp} icon={<FaWhatsapp size={18} />} label="WhatsApp" badge={countOf(props.data, 'messages_whatsapp')} />,
    language: (props) => <Language languages={props.languages} current={props.language} onChangeLanguage={props.onChangeLanguage} />,
    theme: (props) => <ThemeApp theme={props.theme} onChangeTheme={props.onChangeTheme} />,
    notifications: (props) => <Notifications user={props.user} viewAllPath={LINKS.notifications} />,
    profile: (props) => <Profile user={props.user} links={props.profileLinks} onClickLogout={props.onClickLogout} />,
    auth_login: (props) => <AuthAccess path={LINKS.auth_login} label={props.loginLabel} />,
    auth_portal: (props) => <AuthAccess path={LINKS.auth_portal} label={props.portalLabel} />,
};

export const AppHeaderUtilities = ({
    isAuth = false,
    user = null,
    data = {},
    profileLinks = [],
    languages = [],
    language = null,
    onChangeLanguage = null,
    theme = null,
    onChangeTheme = null,
    onClickLogout = null,
}) => {
    const { useDataLanguage } = useLanguage();

    const LOGIN_LABEL = useDataLanguage.translations?.TEMPLATES?.header?.login ?? APP_LAYOUT_STRUCTURE.loginLabel;
    const PORTAL_LABEL = useDataLanguage.translations?.TEMPLATES?.header?.portal ?? APP_LAYOUT_STRUCTURE.portalLabel;

    const PROPS = {
        data, user, profileLinks, languages, language, loginLabel: LOGIN_LABEL, portalLabel: PORTAL_LABEL,
        onChangeLanguage, theme, onChangeTheme, onClickLogout,
    };

    const VISIBLE = APP_LAYOUT_STRUCTURE.utilities.filter((item) => (
        item.isEnabled
        && (!item.requiresAuth || isAuth)
        && (!item.hideWhenAuth || !isAuth)
        && REGISTRY[item.id]
    ));

    if (VISIBLE.length === 0) return null;

    return (
        <HeaderUtilitiesProvider>
            {VISIBLE.map((item) => <span key={item.id}>{REGISTRY[item.id](PROPS)}</span>)}
        </HeaderUtilitiesProvider>
    );
};
